import {
  DEFAULT_UNITS_PREFERENCE,
  catalogBySlug,
  exerciseTraits,
  type EffortScale,
  type TraitsCatalog,
} from '@heraion/shared';
import { buildPayload } from '../workoutPayload';
import { uuidv4 } from '../uuid';
import { noteChanges } from './noteEventReducer';
import type {
  DraftEntry,
  DraftSet,
  LoggerDraft,
  WeightUnit,
} from './loggerDraftTypes';

// Fail-toggle field set (AP-0p model). `fail` is a pure label; toggling it
// always resets the flash flags too. The rep decrement on a fail is a
// separate, explicit step (the fail-confirm popup writes `reps`), not part
// of this flag flip. Centralised so the three toggle sites (cycle_set
// reducer, onToggleFail, effort-commit clear) can't drift.
export const failToggleFields = (
  fail: boolean,
): Pick<DraftSet, 'fail' | 'prFlash' | 'regressionFlash'> => ({
  fail,
  prFlash: false,
  regressionFlash: false,
});

export const emptySet = (
  unit: WeightUnit = DEFAULT_UNITS_PREFERENCE,
  loadedSides: 1 | 2 = 1,
): DraftSet => ({
  reps: '',
  weight: '',
  unit,
  extraNegative: false,
  completedAt: null,
  fail: false,
  prFlash: false,
  note: '',
  rpe: '',
  rir: '',
  loadedSides,
  duration: '',
  distanceM: '',
  avgHrBpm: '',
  calories: '',
  // A blank set carries neither half of AMRAP: no plan asked for it
  // (prescription) and nothing has been performed yet (outcome).
  amrapPrescribed: false,
  amrap: false,
  dropset: null,
});

/** #213: the sign of the extra a set is BORN with, the rule in this file (with
 *  `signAfter` below, its one sibling). A set handed over from another source
 *  (a history set, a prescription, a carried weight) passes its sign only with
 *  a weight. With nothing to take, the catalog decides (`entry.isAssisted`,
 *  i.e. `Exercise.assisted`). */
export const bornExtraNegative = (
  entry: { isAssisted?: boolean },
  from?: Pick<DraftSet, 'weight' | 'extraNegative'> | null,
): boolean =>
  from && from.weight.trim() !== '' ? from.extraNegative : entry.isAssisted === true;

/** The sign of a set born right after another set of the SAME entry: it keeps
 *  that set's sign, weight or not, since turning the chip off is the athlete's
 *  choice for the movement (seedSetFrom). */
export const signAfter = (previous: Pick<DraftSet, 'extraNegative'>): boolean =>
  previous.extraNegative;

/** A blank set of this entry: emptySet with the sign it is born with. */
export const blankSetFor = (
  entry: { isAssisted?: boolean },
  unit: WeightUnit,
  loadedSides: 1 | 2,
): DraftSet => ({
  ...emptySet(unit, loadedSides),
  extraNegative: bornExtraNegative(entry),
});

/** A set handed over whole (a history copy, a repeated session), its sign
 *  settled by the same rule: its own when it has a weight. */
export const withBornSign = (
  entry: { isAssisted?: boolean },
  set: DraftSet,
): DraftSet => ({ ...set, extraNegative: bornExtraNegative(entry, set) });

// LG-71 — has the athlete touched this entry since it was added?
//
// ⭐ The guard on a LATE prefill. When the rolling window cannot see an
// exercise, its sets arrive from the per-exercise index a moment after the card
// does, and the one thing that arrival must never do is grow or rewrite rows
// under the athlete's fingers. So the seed lands only into an entry that is
// still exactly what `add_exercise` minted: one set, nothing typed, nothing
// completed. One tap on ＋, one digit in a cell, one completed set — and the
// answer is dropped in silence, because by then the athlete has said what they
// want and a proposal arriving late would be overwriting them, not helping.
//
// ⛔ Written against `emptySet`'s own output rather than field by field: the
// day a new typed field joins DraftSet, a hand-written list here would keep
// saying "untouched" about a cell that has something in it. `unit` and
// `loadedSides` are excluded because they are the ENTRY's defaults stamped on
// the row, not something the athlete put there — the unit chip flipping to lb
// does not make a blank set a filled one.
const UNTOUCHED_EXCLUDED = new Set(['unit', 'loadedSides']);

// Is this set what the ENTRY's blankSetFor gives, sign included?
const isBlankOf = (entry: DraftEntry, set: DraftSet): boolean => {
  // #213: the blank is the ENTRY's, sign included.
  const blank = blankSetFor(entry, set.unit, set.loadedSides) as unknown as Record<
    string,
    unknown
  >;
  const actual = set as unknown as Record<string, unknown>;
  for (const key of Object.keys(blank)) {
    if (UNTOUCHED_EXCLUDED.has(key)) continue;
    // `undefined` reads as the blank value: the optional fields (rir,
    // regressionFlash) are absent on rows minted by older paths, and an absent
    // optional is not a value the athlete typed.
    const value = actual[key];
    if (value === undefined) continue;
    if (value !== blank[key]) return false;
  }
  return true;
};

export const isPristineNewEntry = (entry: DraftEntry): boolean => {
  const set = entry.sets[0];
  return entry.sets.length === 1 && set !== undefined && isBlankOf(entry, set);
};

/** The catalog rows the kind is completed from: the traits plus, for #41,
 *  whether the exercise itself is timed. */
export type KindCatalog = ReadonlyArray<
  TraitsCatalog[number] & { isTimeBased?: boolean | null }
>;

/** #213: the entries whose `isAssisted` is not known yet (a draft saved before
 *  the flag, a session reopened before the catalog loaded) take it from the
 *  catalog, and their blank sets (judged against the entry as it was) take the
 *  sign they are born with. A set with anything written in it is left alone. */
export const completeAssisted = (
  draft: LoggerDraft,
  catalog: KindCatalog,
): LoggerDraft => {
  if (!draft.entries.some((e) => e.isAssisted === undefined)) return draft;
  const bySlug = catalogBySlug(catalog);
  return {
    ...draft,
    entries: draft.entries.map((entry) => {
      if (entry.isAssisted !== undefined) return entry;
      const row = bySlug.get(entry.exerciseId);
      const traits = exerciseTraits(row);
      // #41: reopened before the catalog, a reps exercise whose sets were all
      // timed read as timed. The row corrects it, as hydrateFromWorkout does
      // with the catalog in hand, and every set stays timed.
      const repsAfterAll = entry.isTimeBased && row !== undefined && row.isTimeBased !== true;
      const completed = {
        ...entry,
        isAssisted: traits.isAssisted,
        ...(repsAfterAll
          ? { isTimeBased: false, isBodyweight: traits.isBodyweight, effortColumn: true }
          : {}),
      };
      return {
        ...completed,
        sets: entry.sets.map((s) => {
          const born = isBlankOf(entry, s)
            ? { ...s, extraNegative: bornExtraNegative(completed) }
            : s;
          return repsAfterAll ? { ...born, timed: true } : born;
        }),
      };
    }),
  };
};

export const initial = (): LoggerDraft => ({
  // #418: one session id per draft, from its first save to its last.
  sessionId: uuidv4(),
  startedAt: new Date().toISOString(),
  entries: [],
});

/** AP-9b: a built «today» draft the athlete has not started: its reasons are
 *  still on it and no set is logged. A logged set starts it like Start does. */
export const isTodayDraftPending = (draft: LoggerDraft): boolean =>
  draft.todayProposal !== undefined &&
  draft.entries.every((e) => e.sets.every((s) => s.completedAt === null));

/** #327: whether saving this correction would write anything the session as
 *  opened does not already hold. Compares what the save sends (buildPayload,
 *  without the suggested name) and the timeline notes it would create, edit
 *  or delete. A draft that does not build counts as changed. */
export const isEditChanged = (
  opened: LoggerDraft,
  draft: LoggerDraft,
  effortScale: EffortScale,
): boolean => {
  const before = buildPayload(opened, effortScale, { dropUncompleted: true });
  const after = buildPayload(draft, effortScale, { dropUncompleted: true });
  if (!before.ok || !after.ok) return true;
  if (JSON.stringify(before.payload) !== JSON.stringify(after.payload)) return true;
  return noteChanges(opened.noteEvents ?? [], draft.noteEvents ?? []).length > 0;
};

export const replaceAt = <T>(arr: T[], i: number, next: T): T[] =>
  arr.map((it, idx) => (idx === i ? next : it));

export const removeAt = <T>(arr: T[], i: number): T[] =>
  arr.filter((_, idx) => idx !== i);

/** Sibling of replaceAt/removeAt: put `item` AT index `i`, pushing the rest
 *  down. `i` is clamped to the array's own bounds, so an out-of-range index
 *  appends rather than producing a hole. */
export const insertAt = <T>(arr: T[], i: number, item: T): T[] => {
  const at = Math.max(0, Math.min(i, arr.length));
  return [...arr.slice(0, at), item, ...arr.slice(at)];
};

/** Apply a pure transform to the entry at `entryIdx`, preserving the
 *  out-of-range no-op (returns `state` unchanged) that every entry-
 *  level reducer case shares. Collapses the get-entry → guard →
 *  replaceAt boilerplate repeated across ~9 actions. */
export function withEntry(
  state: LoggerDraft,
  entryIdx: number,
  fn: (entry: DraftEntry) => DraftEntry,
): LoggerDraft {
  const entry = state.entries[entryIdx];
  if (!entry) return state;
  return {
    ...state,
    entries: replaceAt(state.entries, entryIdx, fn(entry)),
  };
}

/** Apply a pure transform to a single set, preserving the entry+set
 *  out-of-range no-ops. Collapses the nested replaceAt boilerplate
 *  shared by update_set / toggle_unit / toggle_sign / cycle_set. */
export function withSet(
  state: LoggerDraft,
  entryIdx: number,
  setIdx: number,
  fn: (set: DraftSet) => DraftSet,
): LoggerDraft {
  const entry = state.entries[entryIdx];
  if (!entry) return state;
  const set = entry.sets[setIdx];
  if (!set) return state;
  return {
    ...state,
    entries: replaceAt(state.entries, entryIdx, {
      ...entry,
      sets: replaceAt(entry.sets, setIdx, fn(set)),
    }),
  };
}

/** Build the "next set" seed for a given entry following the carry-
 *  forward rules of `add_set`: reps + weight carry from the last set
 *  (weight only when units match — pasting "190" from lb to kg row
 *  silently changes the physical load by 2.2×), unit resets to the
 *  user's preferred unit, completedAt clears, notes/RPE blank, cardio
 *  duration carries forward as a template. Extracted so `add_set` and
 *  the group-wide `add_round_to_group` share the same logic — when one
 *  needs adjusting, both inherit the fix. */
export function seedNextSet(
  entry: DraftEntry,
  defaultUnit: WeightUnit,
): DraftSet {
  return seedSetFrom(entry, entry.sets.at(-1), defaultUnit);
}

/** LG-77 — the seed for a set INSERTED at `beforeIdx` rather than appended.
 *  It carries forward from the set ABOVE the insertion point, which is what
 *  the two gestures Antenore described actually want: a preparation set
 *  between N and N+1 starts from N, and a warm-up inserted before set 1 has
 *  nothing above it, so it is born empty instead of inheriting the working
 *  load. ⛔ Not a default argument on seedNextSet: "append" and "insert" seed
 *  from different rows, and a default would let a caller mean one and get the
 *  other without saying so. */
export function seedInsertedSet(
  entry: DraftEntry,
  beforeIdx: number,
  defaultUnit: WeightUnit,
): DraftSet {
  return seedSetFrom(entry, entry.sets[beforeIdx - 1], defaultUnit);
}

function seedSetFrom(
  entry: DraftEntry,
  last: DraftSet | undefined,
  defaultUnit: WeightUnit,
): DraftSet {
  if (!last) return blankSetFor(entry, defaultUnit, entry.loadedSides);
  const carryWeight = last.unit === defaultUnit;
  return {
    reps: last.reps,
    weight: carryWeight ? last.weight : '',
    unit: defaultUnit,
    extraNegative: signAfter(last),
    completedAt: null,
    fail: false,
    prFlash: false,
    note: '',
    rpe: '',
    rir: '',
    loadedSides: entry.loadedSides,
    // #75: the side the athlete is on carries to the next set.
    ...(last.side ? { side: last.side } : {}),
    duration: entry.isCardio || entry.isTimeBased ? last.duration : '',
    distanceM: '',
    avgHrBpm: '',
    calories: '',
    // Neither half carries onto an extra set. The prescription stops at
    // the plan's last set — an off-plan set was never prescribed AMRAP —
    // and the outcome belongs to the performance about to happen, not to
    // the previous one. Same for the time axis (workPrescribed /
    // restPrescribedSec, HI-1): they are absent here by omission, and this
    // literal builds every field by hand rather than spreading `last`
    // precisely so a prescription can never leak forward by accident.
    amrapPrescribed: false,
    amrap: false,
    dropset: null,
  };
}
